import classnames from 'classnames';
import { isEmpty } from 'ramda';
import React, { FC, memo } from 'react';
import { useFileType } from '../../../utils/hooks/bi/useFileType';
import { MultipleItemsBottomPart } from './BottomParts/MultipleItemsBottomPart';
import { NoItemsBottomPart } from './BottomParts/NoItemsBottomPart';
import { SingleItemBottomPart } from './BottomParts/SingleItemBottomPart';
import styles from './Panel.scss';
import { IOuter } from './Panel.types';
import { MultipleItemsTopPart } from './TopParts/MultipleItemsTopPart';
import { NoItemsTopPart } from './TopParts/NoItemsTopPart';
import { SingleItemTopPart } from './TopParts/SingleItemTopPart';

const PanelComponent: FC<IOuter> = ({
  items,
  selectedItems,
  className,
  source,
  canItemsBeExpanded,
  onFilesExpand,
  downloadSelectedItems,
  isMoreActionsVisible,
  isRestoreVisible,
  isDeleteForeverVisible,
  restoreItems,
  currentFolder,
  onLabelsSave,
  onLabelClick,
  updateItem,
  updateFolderInPath,
  createNewFolderStartFromTop,
  selectItem,
  onRemoveItemsFromBoard,
}) => {
  const fileType = useFileType();

  if (isEmpty(items)) {
    return null;
  }

  const hasNoSelectedItems = isEmpty(selectedItems);
  const hasSingleItemSelected = selectedItems.length === 1;

  const [item] = selectedItems;

  return (
    <div className={styles.panelHolder}>
      <div className={classnames(styles.panel, className)} data-hook="items-info-panel">
        <div className={styles.topPart}>
          {hasNoSelectedItems ? (
            <NoItemsTopPart source={source} currentFolder={currentFolder} />
          ) : hasSingleItemSelected ? (
            <SingleItemTopPart
              item={item}
              currentFolder={currentFolder}
              isExpandable={canItemsBeExpanded}
              onFilesExpand={onFilesExpand}
              source={source}
            />
          ) : (
            <MultipleItemsTopPart isExpandable={canItemsBeExpanded} onFilesExpand={onFilesExpand} items={items} />
          )}
        </div>

        <div className={styles.bottomPart}>
          {hasNoSelectedItems ? (
            <NoItemsBottomPart
              updateItem={updateItem}
              updateFolderInPath={updateFolderInPath}
              currentFolder={currentFolder}
              source={source}
              createNewFolderStartFromTop={createNewFolderStartFromTop}
              isMoreActionsVisible={isMoreActionsVisible}
              selectItem={selectItem}
              onRemoveItemsFromBoard={onRemoveItemsFromBoard}
            />
          ) : hasSingleItemSelected ? (
            <SingleItemBottomPart
              fileType={fileType}
              source={source}
              key={item.id}
              item={item}
              currentFolder={currentFolder}
              onLabelsSave={onLabelsSave}
              onLabelClick={onLabelClick}
              updateItem={updateItem}
              isMoreActionsVisible={isMoreActionsVisible}
              isRestoreVisible={isRestoreVisible}
              isDeleteForeverVisible={isDeleteForeverVisible}
              restoreItems={restoreItems}
              onRemoveItemsFromBoard={onRemoveItemsFromBoard}
            />
          ) : (
            <MultipleItemsBottomPart
              items={selectedItems}
              downloadSelectedItems={downloadSelectedItems}
              isMoreActionsVisible={isMoreActionsVisible}
              isRestoreVisible={isRestoreVisible}
              isDeleteForeverVisible={isDeleteForeverVisible}
              restoreItems={restoreItems}
              source={source}
              onRemoveItemsFromBoard={onRemoveItemsFromBoard}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const Panel: FC<IOuter> = memo<IOuter>(PanelComponent);
